import { useState } from 'react';
import { CheckCircle2, AlertCircle } from 'lucide-react';
import { profileApi } from '../../api/client';

const MIN = 8;

export default function PasswordChangeSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const reset = () => {
    setCurrent(''); setNext(''); setConfirm('');
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;          // belt-and-suspenders against duplicate submits
    setMsg(null);
    if (next.length < MIN) {
      setMsg({ ok: false, text: `Nieuw wachtwoord moet minstens ${MIN} tekens lang zijn.` });
      return;
    }
    if (next !== confirm) {
      setMsg({ ok: false, text: 'De wachtwoorden komen niet overeen.' });
      return;
    }
    if (next === current) {
      setMsg({ ok: false, text: 'Het nieuwe wachtwoord moet verschillen van het huidige.' });
      return;
    }
    setBusy(true);
    try {
      await profileApi.changePassword({ currentPassword: current, newPassword: next });
      setMsg({ ok: true, text: 'Je wachtwoord is gewijzigd.' });
      reset();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setMsg({ ok: false, text: typeof detail === 'string' ? detail : 'Wijziging mislukt.' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="password-change-section" className="mb-10">
      <h2 className="font-serif text-2xl font-light mb-3">Wachtwoord wijzigen</h2>
      <p className="text-sm text-muted-foreground mb-4">
        Kies een sterk wachtwoord van minstens {MIN} tekens. Andere actieve resetlinks worden ongeldig.
      </p>
      <form onSubmit={onSubmit} className="space-y-3 max-w-md" aria-busy={busy}>
        {msg && msg.ok && (
          <div
            data-testid="password-change-success"
            role="status"
            aria-live="polite"
            className="flex items-start gap-2 text-sm px-4 py-3 rounded-lg border bg-emerald-50 border-emerald-200 text-emerald-800 font-medium"
          >
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-px text-emerald-600" aria-hidden="true" />
            <span data-testid="password-change-message">{msg.text}</span>
          </div>
        )}
        {msg && !msg.ok && (
          <div
            data-testid="password-change-error"
            role="alert"
            aria-live="assertive"
            className="flex items-start gap-2 text-sm px-4 py-3 rounded-lg border bg-destructive/10 border-destructive/30 text-destructive font-medium"
          >
            <AlertCircle className="w-5 h-5 shrink-0 mt-px" aria-hidden="true" />
            <span data-testid="password-change-message">{msg.text}</span>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium mb-1">Huidig wachtwoord</label>
          <input
            data-testid="password-change-current"
            type="password" value={current}
            onChange={e => setCurrent(e.target.value)}
            required autoComplete="current-password"
            className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Nieuw wachtwoord</label>
          <input
            data-testid="password-change-new"
            type="password" value={next}
            onChange={e => setNext(e.target.value)}
            required minLength={MIN} autoComplete="new-password"
            className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Bevestig nieuw wachtwoord</label>
          <input
            data-testid="password-change-confirm"
            type="password" value={confirm}
            onChange={e => setConfirm(e.target.value)}
            required minLength={MIN} autoComplete="new-password"
            className="w-full px-4 py-2.5 rounded-lg border border-input bg-card text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
        <button
          data-testid="password-change-submit"
          type="submit" disabled={busy}
          className="bg-primary text-primary-foreground rounded-full py-2.5 px-6 text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-50"
        >
          {busy ? 'Bezig...' : 'Wachtwoord wijzigen'}
        </button>
      </form>
    </section>
  );
}
